import omit from 'lodash/omit'

import { GOOGLE_AUTOMATION_STAGE_STATUSES } from './googleAutomationStageStatuses'
import * as googleUrls from './googleUrls'
import { GoogleSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, GoogleRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import SettingNotFoundException from '../exception/settingNotFoundException'
import {
  AutomationErrors,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, getOptionalElement, shouldUpdateSetting } from '../utils'

const getCurrentValueFn = (key: GoogleSettingKey) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key: GoogleSettingKey) =>
  getIsFulfilledFnFromKey(key, (value) => {
    switch (key) {
      case GoogleSettingKey.BIRTHDAY:
      case GoogleSettingKey.GENDER:
        return value === 'Only you'
      default:
        throw new Error(`Unknown GoogleSettingKey: ${key}`)
    }
  })

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) => getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabled
}

function getIsDisabledReasonFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) => {
    let reason: string | undefined
    switch (settingKey) {
      case GoogleSettingKey.BIRTHDAY:
        reason = 'You do not have a birthday set'
        break
      default:
        break
    }
    return getErrorIsDisabledAndReasonFn(settingKey, reason)(automationErrors)
      .isDisabledReason
  }
}

async function getValueFromTripleButtonGroup(): Promise<string> {
  const selectedButtonEl = await getElement<HTMLButtonElement>(
    document,
    '//button[contains(@aria-label, ", selected")]',
  )
  const value = selectedButtonEl.ariaLabel.replace(/, selected/, '')
  return value
}

async function setTripleButtonGroupValue(value: string): Promise<string> {
  const buttonEl = await getElement<HTMLButtonElement>(
    document,
    `//button[contains(@aria-label, "${value}")]`,
  )
  buttonEl.click()

  return value
}

async function clickSaveForBirthday(userContext) {
  const saveButtonEl = await getOptionalElement<HTMLButtonElement>(
    document,
    '//span[text()="Save"]/..',
    { timeout: 500 },
  )

  // 20250501: The save button may have been removed; we leave here for now
  // in case there are users for whom it is still present.
  //
  // The save button will be disabled if the triple button group is already
  // set to the desired value but the extension state is out of sync
  if (saveButtonEl && !saveButtonEl.disabled && !userContext.isDry) {
    saveButtonEl.click()
  }
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_PROFILE_INFO_BIRTHDAY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.birthdayUrl.navigateForUser(googleAccountIndex)

      const noBirthdayDivEl = await getOptionalElement<HTMLDivElement>(
        document,
        '//h1[contains(text(), "Add your birthday")]',
      )
      if (noBirthdayDivEl) {
        await userContext.updateStoredAutomationErrors(
          {
            [GoogleSettingKey.BIRTHDAY]: {
              gatherError: {
                message: "User doesn't have a birthday set",
                name: SettingNotFoundException.exceptionName,
              },
            },
          },
          GoogleRecommendationKey.GOOGLE_PROFILE_INFO,
        )
        // Unset BIRTHDAY's initial values so update and undo know that the
        // setting is not available
        await userContext.updateStoredInitialValues(
          omit(userContext.initialValues, GoogleSettingKey.BIRTHDAY),
          GoogleRecommendationKey.GOOGLE_PROFILE_INFO,
        )
        return
      }
      const privateEl = await getOptionalElement<HTMLDivElement>(
        document,
        '//div[contains(text(), "This info is private. Only you can see it.")]',
      )
      if (privateEl) {
        await userContext.updateStoredInitialValues(
          {
            [GoogleSettingKey.BIRTHDAY]: 'Only you',
          },
          GoogleRecommendationKey.GOOGLE_PROFILE_INFO,
        )
        return
      }
      const value = await getValueFromTripleButtonGroup()
      await userContext.updateStoredInitialValues(
        {
          [GoogleSettingKey.BIRTHDAY]: value,
        },
        GoogleRecommendationKey.GOOGLE_PROFILE_INFO,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_PROFILE_INFO_GENDER',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.genderUrl.navigateForUser(googleAccountIndex)

      const privateEl = await getOptionalElement<HTMLDivElement>(
        document,
        '//div[contains(text(), "This info is private. Only you can see it.")]',
      )
      if (privateEl) {
        await userContext.updateStoredInitialValues(
          {
            [GoogleSettingKey.GENDER]: 'Only you',
          },
          GoogleRecommendationKey.GOOGLE_PROFILE_INFO,
        )
        return
      }
      const value = await getValueFromTripleButtonGroup()
      await userContext.updateStoredInitialValues(
        {
          [GoogleSettingKey.GENDER]: value,
        },
        GoogleRecommendationKey.GOOGLE_PROFILE_INFO,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_PROFILE_INFO_BIRTHDAY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.birthdayUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.BIRTHDAY,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      await setTripleButtonGroupValue('Only you')
      await clickSaveForBirthday(userContext)
    },
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_PROFILE_INFO_GENDER',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.genderUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.GENDER,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      if (!userContext.isDry) {
        await setTripleButtonGroupValue('Only you')
      }
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_PROFILE_INFO_BIRTHDAY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.birthdayUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.BIRTHDAY,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const initialValue =
        userContext.initialValues?.[GoogleSettingKey.BIRTHDAY]
      if (initialValue) {
        await setTripleButtonGroupValue(initialValue)
        await clickSaveForBirthday(userContext)
      }
    },
  },
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_PROFILE_INFO_GENDER',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.genderUrl.navigateForUser(googleAccountIndex)

      if (
        !shouldUpdateSetting(
          userContext.settingsSelected,
          userContext.initialValues,
          GoogleSettingKey.GENDER,
          getIsFulfilledFn,
        )
      ) {
        return
      }
      const initialValue = userContext.initialValues?.[GoogleSettingKey.GENDER]
      if (!initialValue) {
        throw new Error('UNDO_GOOGLE_PROFILE_INFO_GENDER: No initial value')
      }
      if (!userContext.isDry) {
        await setTripleButtonGroupValue(initialValue)
      }
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
